// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (/welcome/profile → /welcome/consent → /welcome/aboard)
// Final screen — 'Welcome aboard' with the welcome coupon issued by the backend
// (GET /api/me/welcome-coupon), favourite categories and the order-updates switch.
// Only shown right after a mobile login; anyone else is redirected to the home page.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Copy, Fish, Gift, MessageSquareText, Route, ShieldCheck, Shell, Sparkles, Timer } from "lucide-react";
import PageTransition from "../components/PageTransition";
import { SEAFOOD_CATEGORIES, type SeafoodCategory } from "../data/seafood";
import { FACTS } from "../data/welcome";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { fetchWelcomeCoupon, type WelcomeCoupon } from "../utils/coupon";
import { readConsent, saveConsent } from "../utils/consent";
import { readFavourites, saveFavourites } from "../utils/favourites";
import { formatInr } from "../utils/priceFormat";

const CATEGORY_ICONS: Record<SeafoodCategory, typeof Fish> = { Fish, Shellfish: Shell, Premium: Sparkles };

const CATEGORY_LABELS: Record<SeafoodCategory, string> = {
  Fish: "Sea & river fish",
  Shellfish: "Prawns, crab & shellfish",
  Premium: "Premium catch",
};

const TRUST = [
  { icon: Route, title: "Fisherman-direct", detail: `${FACTS.middlemen} middlemen` },
  { icon: ShieldCheck, title: "Traceable catch", detail: `${FACTS.traceability} traced` },
  { icon: Timer, title: "Same-day fresh", detail: `${FACTS.avgDelivery} average` },
];

type CouponState = { status: "loading" } | { status: "error" } | { status: "ready"; coupon: WelcomeCoupon };

/**
 * Shown only straight after a mobile sign-in (the login flow passes `fromLogin` in router
 * state), so a guest, or a later visit to the URL, never lands here.
 */
export default function Aboard() {
  useDocumentTitle("Welcome aboard");
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const arrivedFromLogin = (location.state as { fromLogin?: boolean } | null)?.fromLogin === true;
  const signedIn = auth.status === "signed-in";

  const [picked, setPicked] = useState<SeafoodCategory[]>(readFavourites);
  const [orderUpdates, setOrderUpdates] = useState(readConsent()?.orderUpdates ?? false);
  const [couponState, setCouponState] = useState<CouponState>({ status: "loading" });
  const [copied, setCopied] = useState(false);

  // The backend issues the code (random, one per account) — the client never invents one.
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchWelcomeCoupon().then(
      (coupon) => !cancelled && setCouponState({ status: "ready", coupon }),
      () => !cancelled && setCouponState({ status: "error" }),
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  if (!arrivedFromLogin || auth.status === "signed-out") return <Navigate to="/" replace />;

  const toggle = (category: SeafoodCategory) =>
    setPicked((current) => (current.includes(category) ? current.filter((c) => c !== category) : [...current, category]));

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the code is still on screen to read out.
    }
  };

  const enter = () => {
    saveFavourites(picked);
    const consent = readConsent();
    if (consent) saveConsent({ orderUpdates, seasonalDrops: consent.seasonalDrops });
    navigate("/menu");
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-b from-teal-100/70 via-foam-50 to-foam-50 pt-8 text-ocean-950 dark:from-ocean-900 dark:via-ocean-950 dark:to-ocean-950 dark:text-foam-50">
        <main className="mx-auto flex max-w-xl flex-col gap-6 px-4 pb-12 pt-10">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-lg dark:bg-ocean-800">
              <img src="/logo.png" alt="" className="h-16 w-16 object-contain" />
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight">Welcome aboard!</h1>
            <p className="mt-3 max-w-md text-base leading-relaxed text-ocean-800/85 dark:text-foam-100/80">
              You&apos;re signed in. Fresh catch from partner fishermen, straight to your door.
            </p>
          </div>

          {couponState.status !== "error" && (
            <section
              aria-labelledby="gift-title"
              className="rounded-3xl bg-ocean-800 p-6 text-white shadow-lg dark:bg-ocean-800"
            >
              <p className="inline-flex items-center gap-2 rounded-full bg-coral-500 px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                <Gift className="h-3.5 w-3.5" aria-hidden="true" />
                Welcome gift
              </p>
              <h2 id="gift-title" className="mt-4 font-display text-2xl font-bold leading-tight">
                {couponState.status === "ready"
                  ? `Flat ${formatInr(couponState.coupon.amountOffInr)} off + free delivery`
                  : "Flat ₹200 off + free delivery"}
              </h2>
              <p className="mt-1 text-sm text-foam-100/90">
                {couponState.status === "ready"
                  ? `On your first order of ${formatInr(couponState.coupon.minOrderInr)} or more. Use it once, at checkout.`
                  : "Getting your code…"}
              </p>
              <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-white/10 p-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-foam-100/80">Your coupon code</p>
                  <p className="mt-1 break-all font-mono text-xl font-bold tracking-widest" aria-live="polite">
                    {couponState.status === "ready" ? couponState.coupon.code : "••••••••••"}
                  </p>
                </div>
                {couponState.status === "ready" && !couponState.coupon.redeemed && (
                  <button
                    type="button"
                    onClick={() => copyCode(couponState.coupon.code)}
                    className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-teal-700 px-4 font-bold text-white hover:bg-teal-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  >
                    {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                    {copied ? "Copied" : "Copy code"}
                  </button>
                )}
              </div>
              {couponState.status === "ready" && couponState.coupon.redeemed && (
                <p className="mt-3 text-sm text-foam-100/90">This coupon has already been used.</p>
              )}
            </section>
          )}

          <section aria-labelledby="love-title" className="rounded-3xl bg-white p-6 shadow-lg shadow-ocean-900/5 dark:bg-ocean-900">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 id="love-title" className="font-display text-xl font-bold">
                Tell us what you love
              </h2>
              <span className="text-sm text-ocean-800/80 dark:text-foam-100/70">Tap to choose</span>
            </div>
            <ul className="mt-4 flex flex-wrap gap-3">
              {SEAFOOD_CATEGORIES.map((category) => {
                const Icon = CATEGORY_ICONS[category];
                const on = picked.includes(category);
                return (
                  <li key={category}>
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(category)}
                      className={`inline-flex min-h-11 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                        on ? "bg-ocean-800 text-white" : "bg-ocean-700/10 text-ocean-950 hover:bg-ocean-700/15 dark:bg-white/10 dark:text-foam-50"
                      }`}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {CATEGORY_LABELS[category]}
                      {on && <Check className="h-4 w-4" aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-5 flex items-center gap-4 rounded-2xl bg-ocean-700/5 p-4 dark:bg-white/10">
              <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-whatsapp text-ocean-950">
                <MessageSquareText className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p id="aboard-updates-label" className="font-bold">
                  Order updates on WhatsApp
                </p>
                <p id="aboard-updates-desc" className="text-sm text-ocean-800/85 dark:text-foam-100/80">
                  Delivery status for your orders.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={orderUpdates}
                aria-labelledby="aboard-updates-label"
                aria-describedby="aboard-updates-desc"
                onClick={() => setOrderUpdates((v) => !v)}
                className={`relative h-8 w-14 shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                  orderUpdates ? "bg-teal-700" : "bg-ocean-900/25 dark:bg-white/25"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white shadow transition-transform motion-reduce:transition-none ${
                    orderUpdates ? "translate-x-6" : ""
                  }`}
                />
              </button>
            </div>
          </section>

          <button
            type="button"
            onClick={enter}
            className="inline-flex min-h-14 items-center justify-center gap-2 rounded-full bg-ocean-800 px-8 text-lg font-bold text-white shadow-lg hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            Explore today&apos;s catch
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </button>

          <p className="text-center text-sm">
            <Link to="/account/addresses" className="font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">
              Manage my delivery addresses
            </Link>
          </p>

          <ul className="grid grid-cols-3 gap-2 rounded-2xl bg-ocean-700/5 p-4 text-center dark:bg-white/10">
            {TRUST.map(({ icon: Icon, title, detail }) => (
              <li key={title}>
                <Icon className="mx-auto h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                <p className="mt-1 text-xs font-bold">{title}</p>
                <p className="text-[11px] text-ocean-800/80 dark:text-foam-100/70">{detail}</p>
              </li>
            ))}
          </ul>
        </main>
      </div>
    </PageTransition>
  );
}
